<template>
  <div class="payment-methods-report">
    <v-card flat class="report-page-card">
      <ReportViewBar
        title="Payment Methods"
        :start="start"
        :end="end"
        :export-data="exportData"
        :export-fields="exportFields"
      />

      <v-card-text class="report-content">
        <v-card flat outlined class="report-table-card">
          <v-card-title class="report-table-header">
            <div>
              <div class="report-title">Payment Method Summary</div>
              <div class="report-subtitle">
                {{ Payment.length }} payment methods found
              </div>
            </div>

            <v-spacer />

            <div class="summary-box">
              <div class="summary-label">Total</div>
              <div class="summary-value">{{ total | currency }}</div>
            </div>
          </v-card-title>

          <v-divider />

          <v-card-text class="pa-0">
            <v-progress-linear v-if="isLoading" indeterminate height="3" />

            <v-data-table
              v-else
              dense
              sort-by="paymentMethod"
              readonly
              :mobile-breakpoint="0"
              :headers="headers"
              :items-per-page="-1"
              hide-default-footer
              :items="Payment"
              class="payment-method-table"
            >
              <template v-slot:item.paymentMethod="{ item }">
                <span class="text-capitalize font-weight-medium">
                  {{ item.paymentMethod || "N/A" }}
                </span>
              </template>

              <template v-slot:item.totalPayment="{ item }">
                <span class="money">
                  {{ item.totalPayment | currency }}
                </span>
              </template>

              <template v-slot:body.append v-if="Payment.length > 0">
                <tr class="total-row">
                  <td class="text-right font-weight-bold">
                    Total
                  </td>
                  <td class="text-right font-weight-bold">
                    {{ total | currency }}
                  </td>
                </tr>
              </template>

              <template v-slot:no-data>
                <div class="empty-state">
                  No payment methods found for this date range.
                </div>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import ReportViewBar from "@/components/ReportViewBar";
import paymentService from "@/modules/Customer/Payment/service";

export default {
  name: "payment-methods-report",

  components: {
    ReportViewBar,
  },

  data() {
    return {
      start: null,
      end: null,
      isLoading: true,
      Payment: [],
      exportFields: {
        Method: "paymentMethod",
        Total: "totalPayment",
      },
      headers: [
        {
          text: "Method",
          align: "left",
          value: "paymentMethod",
          sortable: true,
        },
        {
          text: "Total",
          align: "right",
          value: "totalPayment",
          sortable: true,
        },
      ],
    };
  },

  computed: {
    total() {
      return this.Payment.reduce(
        (sum, row) => sum + Number(row.totalPayment || 0),
        0
      );
    },

    exportData() {
      return this.Payment.map((row) => ({
        paymentMethod: row.paymentMethod || "N/A",
        totalPayment: Number(row.totalPayment || 0),
      }));
    },
  },

  async created() {
    this.start = this.$route.query.start || null;
    this.end = this.$route.query.end || null;

    await this.getAll();
  },

  methods: {
    async getAll() {
      const where = {
        start: this.start,
        end: this.end,
      };

      try {
        const response = await paymentService.groupByPaymentMethod(where);
        this.Payment = response.data || [];
      } finally {
        this.$Progress.finish();
        this.isLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.payment-methods-report {
  background: #f5f7fb;
  min-height: 100vh;
  padding: 16px;
}

.report-page-card {
  border-radius: 18px;
  overflow: hidden;
}

.report-content {
  padding: 20px;
}

.report-table-card {
  border-radius: 16px;
  overflow: hidden;
}

.report-table-header {
  background: #fff;
  padding: 18px 20px;
}

.report-title {
  font-size: 18px;
  font-weight: 700;
  color: #1f2937;
}

.report-subtitle {
  font-size: 13px;
  color: #6b7280;
  margin-top: 3px;
}

.summary-box {
  min-width: 120px;
  padding: 8px 12px;
  margin-left: 8px;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  text-align: right;
  background: #f9fafb;
}

.summary-label {
  font-size: 11px;
  color: #6b7280;
  text-transform: uppercase;
}

.summary-value {
  font-size: 15px;
  font-weight: 700;
  color: #111827;
}

.money {
  font-weight: 700;
}

.total-row {
  background: #f9fafb;
}

.empty-state {
  padding: 32px;
  text-align: center;
  color: #6b7280;
}

.payment-method-table ::v-deep th {
  font-size: 12px;
  font-weight: 700;
  color: #374151 !important;
  background: #f9fafb;
  text-transform: uppercase;
}

.payment-method-table ::v-deep td {
  font-size: 13px;
}

@media print {
  .payment-methods-report {
    background: #fff;
    padding: 0;
  }

  .report-page-card,
  .report-table-card {
    box-shadow: none !important;
    border-radius: 0;
  }

  .report-content {
    padding: 0;
  }

  .summary-box {
    border: none;
    background: #fff;
  }

  .no-print {
    display: none !important;
  }
}
</style>